import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from '../../../../../src/hooks/useI18nNavigate';
import { CourseList } from "@edusense/course-ui";
import type { CourseListItem } from "@edusense/course-ui";
import { useCourseDraftData } from '../../../../../hooks/useCourseDraftData';
import type { TransformedCourseDraft } from '../../../../../hooks/useCourseDraftData';
import { useAuth } from '@edusense/shared-auth-core';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import FileText from 'lucide-react/dist/esm/icons/file-text';
import toast from 'react-hot-toast';
import { useCurriculumStore } from '@edusense/entities-course';
import { useDeleteCourseDraft } from '@edusense/course-client';
import { PROFILE_PAGE_TEST_IDS } from '@edusense/shared-constants';

interface ProfileCoursesDraftTabProps {
  userId: string;
  isMyProfile: boolean; // 내 프로필 여부 (UI 표시에 활용 가능)
}

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
  color: var(--theme-text-secondary);
`;

const DraftIcon = styled(FileText)`
  width: 3rem;
  height: 3rem;
  opacity: 0.5;
`;

const ProfileCoursesDraftTab: React.FC<ProfileCoursesDraftTabProps> = ({
  userId,
  isMyProfile,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    data: actualDraftCourses,
    isLoading,
    isFetchingNextPage,
    error,
    hasNextPage,
    fetchNextPage,
    observerRef,
    refresh,
  } = useCourseDraftData({
    filter: 'my_drafts',
    enabled: !!userId && isMyProfile,
  });

  const deleteMutation = useDeleteCourseDraft({
    mutation: {
      onSuccess: () => {
        toast.success('강의 초안이 성공적으로 삭제되었습니다.');
        refresh();
      },
      onError: (error) => {
        console.error('Failed to delete course draft:', error);
        toast.error('강의 초안 삭제에 실패했습니다.');
      },
    }
  });

  // 초안 데이터를 CourseListItem로 변환 (instructor를 현재 사용자로 설정)
  const courses = useMemo(
    () => (actualDraftCourses || []).map((draft): CourseListItem => ({
      id: draft.id,
      title: draft.title || '제목 없음',
      description: draft.description || undefined,
      // 초안은 내가 작성한 것이므로 instructor를 현재 사용자로 설정
      instructor: draft.instructor || (user ? {
        id: user.id,
        username: user.username || '',
        displayName: user.displayName || user.username || '',
        profileImageUrl: user.profileImageUrl,
        verified: user.verified ?? false,
      } : undefined),
      averageRating: draft.averageRating || undefined,
      enrollmentCount: draft.enrollmentCount,
      isFree: true, // 초안은 가격 정보가 없으므로
      level: draft.level || undefined,
      tags: draft.tags || undefined,
      reviewCount: draft.reviewCount || undefined,
      isPublished: draft.isPublished ?? false, // 발행 여부
    })),
    [actualDraftCourses, user]
  );

  // 빈 상태 콘텐츠
  const emptyStateContent = (
    <EmptyStateContainer>
      <DraftIcon />
      <div>
        {isMyProfile
          ? '작성 중인 초안 강의가 없습니다.'
          : '이 사용자의 초안 강의를 볼 수 없습니다.'}
      </div>
    </EmptyStateContainer>
  );

  if (!isMyProfile) {
    return (
      <EmptyStateContainer>
        <DraftIcon />
        <div>다른 사용자의 초안은 볼 수 없습니다.</div>
      </EmptyStateContainer>
    );
  }

  // 편집 핸들러
  const handleEdit = (courseId: string) => {
    const editPath = `/learn/course/${courseId}/edit`;
    navigate(editPath);
  };

  // 삭제 핸들러
  const handleDelete = (courseId: string) => {
    deleteMutation.mutate({ id: courseId });

    // 삭제된 초안이 현재 편집 중인 초안인지 확인
    const curriculumState = useCurriculumStore.getState();
    if (curriculumState.currentEditingCourseId === courseId) {
      curriculumState.resetCurriculumState();
      toast('현재 편집 중이던 초안의 로컬 학습 내용이 정리되었습니다.');
    }
  };

  return (
    <CourseList
      courses={courses}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      error={error}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      observerRef={observerRef}
      emptyStateContent={emptyStateContent}
      itemDataTestId={PROFILE_PAGE_TEST_IDS.PROFILE_COURSES_LIST_ITEM}
      onCardClick={handleEdit} // 초안 카드 클릭 시 편집 페이지로 이동
      onEdit={handleEdit}
      onDelete={handleDelete}
      isDraft={true}
    />
  );
};

export default ProfileCoursesDraftTab; 
